import { IPromise, IQService, IScope } from "angular";
import { UIRouterGlobals } from "@uirouter/angularjs";
import { first } from "rxjs";
import { IRestLayerRequest } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { expandedGoalsScreenPath, filtersOverrideForOkrName, filtersOverrideForTagTitle } from "@gtmhub/goals/filter-utils";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { CloneOkrsModal } from "@gtmhub/okrs/components/clone-okrs/clone-okrs.component";
import { GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { expandedGoalsOkrViewScreenPath } from "@gtmhub/okrs/okr-views/utils/okr-view-utils";
import { BASE_OKR_VIEW_STATE } from "@gtmhub/okrs/okrs.states";
import { Ng1ConfirmGoalDeleteService } from "@gtmhub/okrs/services/ng1-confirm-goal-delete.service";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { IGridSortingRule, OrFiltersGroup, RestLayerFilterFactory, hasFilterValue, toRestLayerRequest } from "@gtmhub/shared/components/grid-filtering-bar";
import { separateDynamicValuesForAllGroups } from "@gtmhub/shared/filters/separate-dynamic-filters-util";
import { Ng1ConfirmDeleteService } from "@gtmhub/shared/services/ng1-confirm-delete.service";
import { INgRedux } from "@gtmhub/state-management";
import { PermissionsService } from "@gtmhub/users/permissions-service";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { CloneMoveOkrModalService } from "@webapp/okrs/components/clone-move-okrs/clone-move-okr-modal.service";
import { MetricMilestoneEventType } from "@webapp/okrs/metrics/models/metric.models";
import { IUserItemAllowedActions, Session } from "@webapp/sessions/models/sessions.model";
import { AlignmentContentState, AlignmentLoader, IAlignmentLoadedEventArgs } from "../alignment-loader";
import { IAlignmentTreeFilterResult, INestedTreeGoal, INestedTreeOkrNode } from "../models";
import { NestedTreeService } from "../services/nested-tree.service";
import { resetPersistedExpandedItems } from "../utils";
import { IAlignmentSidebarStateParamsService } from "./alignment-sidebar-ctrl";

interface INestedTreeReduxScope {
  sessionsIdMap: Record<string, Session>;
}

const bindStateToScope = (state: ISessionsStoreState) => {
  return {
    sessionsIdMap: state.sessions.map,
  };
};

export interface IAlignmentNestedScope extends IScope, INestedTreeReduxScope {
  indicators: {
    loading?: IIndicator;
  };
  allowedActions: string[];
  showMetrics: boolean;
  confirmDeleteGoal(goal: IGoal): void;

  okrsTreeRoot: INestedTreeOkrNode;
  planningSession: Session;
  areRootGoalsExpandable: boolean;
  isProgressAggregationDisabled: boolean;
  sorted: boolean;
  confirmDeleteMetric(goalId: string, metricId: string);
  openCloneGoalModal(goalId: string);
  openCloneMetricModal(metricId: string);
  persistExpandedOkr(okrId: string, isExpanded: { isExpanded: boolean });
  isNewCloneAndMoveModalEnabled: boolean;
}

export class AlignmentNestedCtrl implements IStateInit {
  private currentSessionId: string;
  private order: IGridSortingRule[];
  private goals: IGoal[];
  private filters: OrFiltersGroup[];
  private contentState: AlignmentContentState;
  private okrEvents: OkrEvents;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$q",
    "GoalService",
    "PermissionsService",
    "AlignmentLoader",
    "UIErrorHandlingService",
    "MetricService",
    "CloneOkrsModal",
    "NestedTreeService",
    "PlanningSessionsActions",
    "$ngRedux",
    "OkrMethodologyFacade",
    "$uiRouterGlobals",
    "$stateParams",
    "FeatureTogglesFacade",
    "CloneMoveOkrModalService",
    "Ng1ConfirmDeleteService",
    "Ng1ConfirmGoalDeleteService",
  ];

  constructor(
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $scope: IAlignmentNestedScope,
    private $q: IQService,
    private goalService: GoalService,
    private permissionsService: PermissionsService,
    private alignmentLoader: AlignmentLoader,
    private uiErrorHandlingService: UIErrorHandlingService,
    private metricService: MetricService,
    private cloneOkrsModal: CloneOkrsModal,
    private nestedTreeService: NestedTreeService,
    private planningSessionsActions: PlanningSessionsActions,
    private $ngRedux: INgRedux,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private routerGlobals: UIRouterGlobals,
    private $stateParams: IAlignmentSidebarStateParamsService,
    private featureToggleService: FeatureTogglesFacade,
    private cloneMoveModalService: CloneMoveOkrModalService,
    private confirmDeleteService: Ng1ConfirmDeleteService,
    private confirmGoalDeleteService: Ng1ConfirmGoalDeleteService
  ) {
    this.$rootScope.noNavigation = false;
    this.okrEvents = new OkrEvents(this.$scope);

    this.$scope.confirmDeleteGoal = this.confirmDeleteGoal;
    this.$scope.confirmDeleteMetric = this.confirmDeleteMetric;
    this.$scope.openCloneGoalModal = this.openCloneGoalModal;
    this.$scope.openCloneMetricModal = this.openCloneMetricModal;
    this.$scope.persistExpandedOkr = this.persistExpandedOkr;

    this.$scope.indicators = {};

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey || e.altKey) && e.keyCode === 80) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.body.addEventListenerOutsideAngular("keydown", onKeyDown);
    this.$scope.$on("$destroy", () => document.removeEventListener("keydown", onKeyDown));

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)($scope);
    $scope.$on("$destroy", unsubscribe);

    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
  }

  public stateInit(): IPromise<unknown> {
    return this.alignmentLoader.promise().then((args) => this.$rootScope.traceAction("nested_tree_init", () => this.onLoaded(args)));
  }

  private onLoaded = (args: IAlignmentLoadedEventArgs): void => {
    this.evaluateCtrlVariables(args);

    this.$scope.$on("alignment.unifiedFiltersChanged", (evt, args: IAlignmentLoadedEventArgs) => this.applyUnifiedFiltersChanges(args));

    // local events
    this.$scope.$on("goalsChanged", () => {
      this.loadGoals();
    });

    this.okrEvents.onGoalsChanged((args) => this.handleGoalsChangedEvent(args));

    // directive events
    this.$scope.$on("goalsTree.confirmDeleteGoal", (evt, goal: IGoal) => this.confirmDeleteGoal(goal));

    // component events
    this.$scope.$on("nestedTree.confirmDeleteGoal", (evt, goal: IGoal) => this.$scope.confirmDeleteGoal(goal));
    this.$scope.$on("nestedTree.confirmDeleteMetric", (evt, goalId: string, metricId: string) => this.$scope.confirmDeleteMetric(goalId, metricId));
    this.$scope.$on("nestedTree.cloneGoal", (evt, goalId: string) => this.$scope.openCloneGoalModal(goalId));
    this.$scope.$on("nestedTree.cloneMetric", (evt, metricId: string) => this.$scope.openCloneMetricModal(metricId));
    this.$scope.$on("nestedTree.okrExpandToggled", (evt, okrId: string, { isExpanded }: { isExpanded: boolean }) =>
      this.$scope.persistExpandedOkr(okrId, { isExpanded })
    );

    // sidebar events
    this.$scope.$on("nested.viewModeChanged", (evt, { showMetrics }: { showMetrics: boolean }) => this.showMetricsChanged(showMetrics));
    this.$scope.$on("alignment.expandAll", () => this.expandAll());
    this.$scope.$on("alignment.collapseAll", () => this.collapseAll());
    this.$scope.$on("alignment.orderChanged", (evt, orderRules: { order: IGridSortingRule[] }) => this.orderChanged(orderRules.order));

    this.checkPermissions();
    this.loadGoals();

    this.$scope.planningSession = this.$scope.sessionsIdMap[this.currentSessionId];
    this.$scope.isProgressAggregationDisabled = this.okrMethodologyFacade.isProgressAggregationDisabled(this.$scope.planningSession?.settings);
    this.checkA14CloneAndMoveModalsFeatureToggle();
  };

  private evaluateCtrlVariables = (args: IAlignmentLoadedEventArgs): void => {
    this.currentSessionId = args.currentSessionId;
    this.order = args.order;
    this.filters = args.filters;
    this.$scope.showMetrics = args.viewModes.showMetrics;
    this.contentState = args.contentState;
  };

  private checkPermissions = (): void => {
    this.permissionsService.getCurrentUserAllowedActionsPerItem(this.currentSessionId, "session").then(
      (actions: IUserItemAllowedActions) => {
        this.$scope.allowedActions = actions.currentUserActions;
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private expandAll = (): void => {
    this.nestedTreeService.expandAllNodes(this.currentSessionId, this.$scope.okrsTreeRoot);
  };

  private collapseAll = (): void => {
    this.nestedTreeService.collapseAllNodes(this.currentSessionId, this.$scope.okrsTreeRoot);
  };

  private loadGoals = (): void => {
    this.$scope.indicators.loading = { progress: true };
    this.goalService
      .getAllGoalsV2({ filter: { sessionId: this.currentSessionId }, limit: 200 })
      .then((collection) => {
        this.goals = collection.items;
        return this.recalculateGoalsTree();
      })
      .then(
        () => {
          this.$scope.indicators.loading = null;
        },
        (error) => (this.$scope.indicators.loading = { error })
      );
  };

  private showMetricsChanged = (showMetrics: boolean): void => {
    this.$scope.showMetrics = showMetrics;
    this.recalculateGoalsTree().catch((error) => this.uiErrorHandlingService.handleModal(error));
  };

  private orderChanged = (order: IGridSortingRule[]): void => {
    this.order = order;
    this.recalculateGoalsTree()
      .then(() => {
        this.$scope.sorted = true;
      })
      .catch((error) => this.uiErrorHandlingService.handleModal(error));
  };

  private recalculateGoalsTree = () => {
    let goalsRequest: IRestLayerRequest;
    const hasNonSessionFilters = this.filters.some((filtersGroup) => filtersGroup.some((filter) => filter.fieldName !== "sessionId" && hasFilterValue(filter)));
    if (hasNonSessionFilters) {
      const filtersOverride: Record<string, RestLayerFilterFactory> = {
        ...filtersOverrideForTagTitle(),
        ...filtersOverrideForOkrName(),
      };

      const separatedFilterValues: OrFiltersGroup[] = separateDynamicValuesForAllGroups(angular.copy(this.filters));
      goalsRequest = toRestLayerRequest({ filters: separatedFilterValues, filtersOverride });
    }

    let filterPromise: IPromise<IAlignmentTreeFilterResult>;
    if (goalsRequest) {
      goalsRequest.fields = "_id";
      goalsRequest.limit = -1;

      filterPromise = this.goalService.getGoalsV2(goalsRequest).then((matches) => ({ goalIds: matches.items.map((x) => x.id) }));
    } else {
      filterPromise = this.$q.resolve<IAlignmentTreeFilterResult>(undefined);
    }

    return filterPromise.then((filterResult) => {
      const matchedGoalIds = filterResult ? filterResult.goalIds : [];

      this.$scope.okrsTreeRoot = this.nestedTreeService.constructNestedViewOkrTree({
        goals: this.goals as INestedTreeGoal[],
        sort: this.order,
        matchedGoalIds: matchedGoalIds,
        filtersApplied: hasNonSessionFilters,
        showMetrics: this.$scope.showMetrics,
        sessionId: this.currentSessionId,
      });

      this.$scope.areRootGoalsExpandable = this.$scope.okrsTreeRoot.children.some((rootNode) => rootNode.children.length > 0);
      this.contentState.hasItems = this.$scope.okrsTreeRoot.children.length > 0;
    });
  };

  private confirmDeleteMetric = (goalId: string, metricId: string) => {
    this.confirmDeleteService.confirmDeleteItem({
      itemKey: "metric",
      action: () => Promise.resolve(this.deleteMetric(goalId, metricId)),
    });
  };

  private confirmDeleteGoal = (goal: IGoal) => {
    this.confirmGoalDeleteService.confirmGoalDelete({
      action: () => Promise.resolve(this.deleteGoal(goal)),
    });
  };

  private deleteMetric = (goalId: string, metricId: string): IPromise<void> => {
    return this.$rootScope.traceAction("delete_metric", () => {
      this.metricService.deleteMetric(goalId, metricId).then(() => {
        this.trackMetricMilestoneDelete(goalId, metricId);
        this.$rootScope.$broadcast("goalsChanged");
      });
    });
  };

  private trackMetricMilestoneDelete = (goalId: string, metricId: string): void => {
    this.featureToggleService
      .isFeatureAvailable$(FeatureFlag.MetricMilestone)
      .pipe(first())
      .subscribe((isFeatureEnabled) => {
        if (isFeatureEnabled) {
          const metric = this.goals?.find((goal) => goal.id === goalId)?.metrics?.find((metric) => metric.id === metricId);

          if (!metric) {
            return;
          }

          this.metricService.trackMetricMilestone(MetricMilestoneEventType.KR_MILESTONES_DELETED, { metric });
        }
      });
  };

  private deleteGoal = (goal: IGoal): IPromise<void> => {
    return this.$rootScope.traceAction("delete_goal", () => {
      this.goalService.deleteGoal(goal.id).then(() => {
        this.$rootScope.$broadcast("goalsChanged");
      });
    });
  };

  private checkA14CloneAndMoveModalsFeatureToggle = (): IPromise<void> => {
    return this.featureToggleService.isFeatureAvailable(FeatureFlag.CloneAndMoveOkrModal).then((isNewCloneAndMoveModalEnabled) => {
      this.$scope.isNewCloneAndMoveModalEnabled = isNewCloneAndMoveModalEnabled;
    });
  };

  private openCloneMetricModal = (metricId: string) => {
    this.metricService
      .getMetric(metricId)
      .then((metric) => {
        if (this.$scope.isNewCloneAndMoveModalEnabled) {
          this.cloneMoveModalService.openCloneKrModal(metric);
        } else {
          this.cloneOkrsModal.openForMetricClone(metric);
        }
      })
      .catch((error) => this.uiErrorHandlingService.handleModal(error));
  };

  private openCloneGoalModal = (goalId: string) => {
    this.goalService
      .getGoal(goalId)
      .then((goal) => {
        if (this.$scope.isNewCloneAndMoveModalEnabled) {
          this.cloneMoveModalService.openCloneOkrModal(goal);
        } else {
          this.cloneOkrsModal.openForGoalClone(goal);
        }
      })
      .catch((error) => this.uiErrorHandlingService.handleModal(error));
  };

  private applyUnifiedFiltersChanges = (args: IAlignmentLoadedEventArgs): void => {
    this.evaluateCtrlVariables(args);
    resetPersistedExpandedItems({ entityId: this.getEntityId(), screenPath: this.getExpandedGoalsScreenPath() });
    this.recalculateGoalsTree().catch((error) => this.uiErrorHandlingService.handleModal(error));
  };

  private persistExpandedOkr = (okrId: string, { isExpanded }: { isExpanded: boolean }): void => {
    this.nestedTreeService.toggleExpandItem(this.currentSessionId, okrId, { isExpanded });
  };

  private handleGoalsChangedEvent = (args: GoalsChangedEventArgs) => {
    switch (args.reason) {
      case "goalCreated": {
        const goalParentId = args.detail.goal.parentId;
        if (goalParentId) {
          this.persistExpandedOkr(goalParentId, { isExpanded: true });
        }
        break;
      }
      case "metricCreated": {
        const metricParentId = args.detail.goalId;
        this.persistExpandedOkr(metricParentId, { isExpanded: true });
        break;
      }
      case "goalChildReordered":
        this.loadGoals();
        break;
    }
  };

  private getExpandedGoalsScreenPath = (): string[] => {
    if (this.checkIfTreeIsLoadedFromOkrView()) {
      return expandedGoalsOkrViewScreenPath(this.$stateParams.okrViewId);
    }

    return expandedGoalsScreenPath(this.currentSessionId);
  };

  private getEntityId = (): string => {
    if (this.checkIfTreeIsLoadedFromOkrView()) {
      return this.$stateParams.okrViewId;
    }

    return this.currentSessionId;
  };

  private checkIfTreeIsLoadedFromOkrView = (): boolean => {
    return this.routerGlobals.current.name.startsWith(BASE_OKR_VIEW_STATE);
  };
}
